import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { TrendingUp, Zap, Shield, BarChart2, Copy, Check, ArrowRight } from 'lucide-react';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Roboto+Slab:wght@900&family=Montserrat:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;700&display=swap');

  * { margin: 0; padding: 0; box-sizing: border-box; }

  .impl-page {
    min-height: 100vh;
    background-color: var(--bg-app);
    color: var(--text-primary);
    font-family: 'DM Sans', sans-serif;
  }

  .impl-wrap {
    max-width: 52rem;
    margin: 0 auto;
    padding: 4rem 2rem 6rem;
  }

  .impl-hero {
    margin-bottom: 5rem;
    border-bottom: 1px solid var(--border-subtle);
    padding-bottom: 3.5rem;
  }

  .impl-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 0.75rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #D97642;
    margin-bottom: 1.5rem;
  }

  .impl-eyebrow span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #D97642;
    display: inline-block;
  }

  .impl-h1 {
    font-family: 'Abril Fatface', serif;
    font-size: clamp(2.25rem, 4.5vw, 3.5rem);
    line-height: 1.1;
    margin-bottom: 1.5rem;
    color: var(--text-primary);
  }

  .impl-lead {
    font-size: 1.125rem;
    line-height: 1.75;
    color: var(--text-secondary);
    max-width: 42rem;
  }

  .impl-section {
    margin-bottom: 4.5rem;
  }

  .impl-section-label {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 0.75rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--text-muted);
    margin-bottom: 1rem;
  }

  .impl-h2 {
    font-family: 'Montserrat', sans-serif;
    font-weight: 800;
    font-size: 1.75rem;
    margin-bottom: 1.25rem;
    color: var(--text-primary);
  }

  .impl-body {
    font-size: 1.0625rem;
    line-height: 1.75;
    color: var(--text-secondary);
    margin-bottom: 1.5rem;
  }

  .benefit-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
    margin-top: 2rem;
  }

  .benefit-card {
    padding: 1.75rem;
    background: var(--bg-surface);
    border: 1px solid var(--border-subtle);
    border-radius: 0.75rem;
  }

  .benefit-icon { margin-bottom: 1rem; }

  .benefit-title {
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 1rem;
    margin-bottom: 0.5rem;
    color: var(--text-primary);
  }

  .benefit-text {
    font-size: 0.9375rem;
    line-height: 1.65;
    color: var(--text-secondary);
  }

  .steps {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-top: 2rem;
  }

  .step-row {
    display: flex;
    gap: 1.5rem;
    position: relative;
  }

  .step-row:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 1.25rem;
    top: 3rem;
    bottom: -0.5rem;
    width: 1px;
    background: var(--border-subtle);
  }

  .step-num {
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    background: linear-gradient(135deg, #D97642, #B85F2F);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Montserrat', sans-serif;
    font-weight: 800;
    font-size: 1rem;
    color: white;
    margin-top: 0.125rem;
  }

  .step-body { flex: 1; padding-bottom: 2.5rem; }

  .step-title {
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 1.0625rem;
    margin-bottom: 0.375rem;
    color: var(--text-primary);
  }

  .step-text {
    font-size: 0.9375rem;
    line-height: 1.65;
    color: var(--text-secondary);
  }

  .code-wrap {
    background: #0D1110;
    border: 1px solid var(--border-subtle);
    border-radius: 0.75rem;
    overflow: hidden;
    margin-top: 2rem;
  }

  .code-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.875rem 1.25rem;
    border-bottom: 1px solid var(--border-subtle);
    background: rgba(255,255,255,0.03);
  }

  .code-lang {
    font-family: 'Montserrat', sans-serif;
    font-weight: 600;
    font-size: 0.8125rem;
    color: #9AA3AB;
    letter-spacing: 0.05em;
  }

  .code-tabs { display: flex; gap: 0.25rem; }

  .code-tab {
    padding: 0.35rem 0.875rem;
    font-family: 'Montserrat', sans-serif;
    font-weight: 600;
    font-size: 0.8125rem;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 0.375rem;
    color: #6B7280;
    cursor: pointer;
    transition: all 0.15s;
  }

  .code-tab.active {
    background: rgba(217, 118, 66, 0.15);
    border-color: rgba(217, 118, 66, 0.3);
    color: #D97642;
  }

  .code-copy {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.35rem 0.875rem;
    font-family: 'Montserrat', sans-serif;
    font-weight: 600;
    font-size: 0.8125rem;
    background: transparent;
    border: 1px solid var(--border-subtle);
    border-radius: 0.375rem;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.15s;
  }

  .code-copy:hover { border-color: #5FD4A0; color: #5FD4A0; }

  .code-body { padding: 1.5rem; overflow-x: auto; }

  .code-body code {
    font-family: 'Courier New', monospace;
    font-size: 0.875rem;
    line-height: 1.7;
    color: #C2BDB0;
    white-space: pre;
    display: block;
  }

  .response-fields {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1.5rem;
    padding: 1.5rem;
    background: var(--bg-surface);
    border: 1px solid var(--border-subtle);
    border-radius: 0.75rem;
  }

  .r-field { display: flex; gap: 1rem; align-items: flex-start; }

  .r-key {
    font-family: 'Courier New', monospace;
    font-size: 0.875rem;
    color: #5FD4A0;
    font-weight: 600;
    min-width: 9rem;
    flex-shrink: 0;
  }

  .r-desc {
    font-size: 0.9375rem;
    color: var(--text-secondary);
    line-height: 1.55;
  }

  .proof-strip {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1px;
    background: var(--border-subtle);
    border: 1px solid var(--border-subtle);
    border-radius: 0.75rem;
    overflow: hidden;
    margin-top: 2rem;
  }

  .proof-item {
    background: var(--bg-surface);
    padding: 1.75rem 1.5rem;
    text-align: center;
  }

  .proof-number {
    font-family: 'Montserrat', sans-serif;
    font-weight: 800;
    font-size: 2rem;
    color: #D97642;
    display: block;
    margin-bottom: 0.375rem;
  }

  .proof-label {
    font-size: 0.875rem;
    color: var(--text-secondary);
    line-height: 1.4;
  }

  .impl-cta {
    margin-top: 5rem;
    padding: 3rem;
    background: var(--bg-surface);
    border: 1px solid var(--border-subtle);
    border-radius: 1rem;
    text-align: center;
  }

  .impl-cta-title {
    font-family: 'Montserrat', sans-serif;
    font-weight: 800;
    font-size: 1.75rem;
    margin-bottom: 0.875rem;
    color: var(--text-primary);
  }

  .impl-cta-text {
    font-size: 1rem;
    color: var(--text-secondary);
    margin-bottom: 2rem;
    line-height: 1.65;
  }

  .impl-cta-btns {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
  }

  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.875rem 2rem;
    background: #D97642;
    color: white;
    border: none;
    border-radius: 0.5rem;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 0.9375rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-primary:hover { background: #C56838; transform: translateY(-1px); }

  .btn-secondary {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.875rem 2rem;
    background: transparent;
    color: var(--text-primary);
    border: 1px solid var(--border-subtle);
    border-radius: 0.5rem;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 0.9375rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-secondary:hover { border-color: var(--text-primary); }

  @media (max-width: 768px) {
    .impl-wrap { padding: 2.5rem 1.25rem 4rem; }
    .impl-h1 { font-size: 2rem; }
    .benefit-grid { grid-template-columns: 1fr; }
    .proof-strip { grid-template-columns: 1fr; }
    .impl-cta { padding: 2rem 1.5rem; }
    .r-field { flex-direction: column; gap: 0.25rem; }
    .r-key { min-width: unset; }
  }
`;

const codeExamples = {
  curl: `curl -X POST https://mizar-api.vercel.app/v1/signal \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "origin": "LHR",
    "destination": "BCN",
    "outbound_date": "2026-07-15",
    "return_date": "2026-07-22",
    "price_gbp": 285.50,
    "trip_type": "return",
    "cabin_class": "economy"
  }'`,
  javascript: `const response = await fetch(
  'https://mizar-api.vercel.app/v1/signal',
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer YOUR_API_KEY',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      origin: 'LHR',
      destination: 'BCN',
      outbound_date: '2026-07-15',
      return_date: '2026-07-22',
      price_gbp: 285.50,
      trip_type: 'return',
      cabin_class: 'economy'
    })
  }
);

const data = await response.json();
const { regret_risk_score, recommendation, confidence_score } = data;`,
  python: `import requests

response = requests.post(
    'https://mizar-api.vercel.app/v1/signal',
    headers={
        'Authorization': 'Bearer YOUR_API_KEY',
        'Content-Type': 'application/json'
    },
    json={
        'origin': 'LHR',
        'destination': 'BCN',
        'outbound_date': '2026-07-15',
        'return_date': '2026-07-22',
        'price_gbp': 285.50,
        'trip_type': 'return',
        'cabin_class': 'economy'
    }
)

data = response.json()`,
};

const apiResponseExample = `{
  "regret_risk_score": 0.73,
  "recommendation": "book_now",
  "confidence": "high",
  "confidence_score": 0.85,
  "signal_stability": "stable",
  "snapshot_age_hours": 7,
  "snapshot_count_30d": 27,
  "volatility_7d": 0.337,
  "decision_id": "550e8400-e29b-41d4-a716-446655440000",
  "route": "LHR → BCN",
  "model_version": "v3_1_0",
  "signal_shown": true
}`;

export default function Implementation() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('curl');
  const [copied, setCopied] = useState('');

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <div className="impl-page">
      <style>{styles}</style>
      <Navigation />

      <div className="impl-wrap">

        <div className="impl-hero">
          <div className="impl-eyebrow"><span />Flight Price Prediction API</div>
          <h1 className="impl-h1">One API call.<br />A safer way to test fare-risk signals.</h1>
          <p className="impl-lead">
            MIZAR&apos;s RegretRisk API scores a flight fare at the point of search and returns
            the probability that the fare will rise by 10% or more within seven days.
            In Shadow Mode, travel sellers can log these signals alongside their existing
            booking flow without changing pricing, checkout, or customer experience.
          </p>
        </div>

        <section className="impl-section">
          <div className="impl-section-label">
            <TrendingUp size={14} color="#D97642" />
            What MIZAR does
          </div>
          <h2 className="impl-h2">A risk score at the moment of search</h2>
          <p className="impl-body">
            Travellers often hesitate at the point of booking because they do not know whether
            the fare in front of them is likely to rise or fall. MIZAR quantifies that fare-risk
            window and returns a calibrated RegretRisk score for the current route, dates, and price.
          </p>
          <p className="impl-body">
            You pass the route, travel dates, trip type, cabin class, and current fare to the API.
            MIZAR returns a probability score, recommendation, confidence metadata, and decision ID.
            During Shadow Mode, your team can log the response internally and review whether
            high-risk signals align with subsequent fare movement before exposing anything to users.
          </p>

          <div className="benefit-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Zap size={20} color="#D97642" /></div>
              <div className="benefit-title">Shadow Mode first</div>
              <div className="benefit-text">
                Evaluate signal quality and commercial relevance without changing the customer
                journey. No checkout modification is required during the observation phase.
              </div>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={20} color="#D97642" /></div>
              <div className="benefit-title">Low operational risk</div>
              <div className="benefit-text">
                MIZAR does not modify fares, process payments, or require customer identity data.
                The pilot can run as an external scoring layer.
              </div>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><BarChart2 size={20} color="#D97642" /></div>
              <div className="benefit-title">Validated evidence</div>
              <div className="benefit-text">
                The production model is continuously verified against observed fare outcomes through an independent outcome validation process.
              </div>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><TrendingUp size={20} color="#D97642" /></div>
              <div className="benefit-title">Built for online travel sellers</div>
              <div className="benefit-text">
                The API is designed to help booking platforms evaluate where fare uncertainty
                may affect booking behaviour and commercial decision-making.
              </div>
            </div>
          </div>
        </section>

        <section className="impl-section">
          <div className="impl-section-label">
            <Zap size={14} color="#D97642" />
            Integration
          </div>
          <h2 className="impl-h2">A single scoring request</h2>
          <p className="impl-body">
            There is no SDK to install, no proprietary tracking script, and no checkout dependency.
            The current integration surface is a single authenticated POST request to the MIZAR
            signal endpoint.
          </p>

          <div className="steps">
            <div className="step-row">
              <div className="step-num">1</div>
              <div className="step-body">
                <div className="step-title">Request API access</div>
                <div className="step-text">
                  MIZAR provides an API key for the agreed discovery or pilot scope. Access can be
                  limited to selected routes during the initial pilot.
                </div>
              </div>
            </div>
            <div className="step-row">
              <div className="step-num">2</div>
              <div className="step-body">
                <div className="step-title">Call /v1/signal at search or fare display</div>
                <div className="step-text">
                  Send route details, dates, trip type, cabin class, and displayed fare to the
                  scoring endpoint. The response can run in parallel with your existing booking flow.
                </div>
              </div>
            </div>
            <div className="step-row">
              <div className="step-num">3</div>
              <div className="step-body">
                <div className="step-title">Log the response in Shadow Mode</div>
                <div className="step-text">
                  During the pilot, no customer-facing display is required. Your team can log the
                  score, recommendation, confidence metadata, and decision ID internally for review.
                </div>
              </div>
            </div>
            <div className="step-row">
              <div className="step-num">4</div>
              <div className="step-body">
                <div className="step-title">Review fare outcomes</div>
                <div className="step-text">
                  After the observation window, scores can be compared with subsequent fare movement
                  to assess whether high-risk signals identified commercially relevant moments.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="impl-section">
          <div className="impl-section-label">
            <Zap size={14} color="#D97642" />
            The API call
          </div>
          <h2 className="impl-h2">One endpoint. Any language.</h2>
          <p className="impl-body">
            POST route and fare context. Receive a RegretRisk score, recommendation, confidence
            metadata, and decision ID.
          </p>

          <div className="code-wrap">
            <div className="code-top">
              <div className="code-tabs">
                {['curl', 'javascript', 'python'].map(tab => (
                  <button
                    key={tab}
                    className={"code-tab" + (activeTab === tab ? " active" : "")}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab === 'curl' ? 'cURL' : tab === 'javascript' ? 'JavaScript' : 'Python'}
                  </button>
                ))}
              </div>
              <button className="code-copy" onClick={() => copy(codeExamples[activeTab], 'example')}>
                {copied === 'example' ? <Check size={14} /> : <Copy size={14} />}
                {copied === 'example' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div className="code-body">
              <code>{codeExamples[activeTab]}</code>
            </div>
          </div>

          <div className="code-wrap" style={{marginTop: '1.5rem'}}>
            <div className="code-top">
              <span className="code-lang">JSON RESPONSE</span>
              <button className="code-copy" onClick={() => copy(apiResponseExample, 'response')}>
                {copied === 'response' ? <Check size={14} /> : <Copy size={14} />}
                {copied === 'response' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div className="code-body">
              <code>{apiResponseExample}</code>
            </div>
          </div>

          <div className="response-fields">
            <div className="r-field">
              <span className="r-key">regret_risk_score</span>
              <span className="r-desc">Calibrated probability from 0.0 to 1.0 that the fare rises 10% or more within seven days.</span>
            </div>
            <div className="r-field">
              <span className="r-key">recommendation</span>
              <span className="r-desc">Operational recommendation returned by the production model, such as book_now, consider, or monitor.</span>
            </div>
            <div className="r-field">
              <span className="r-key">confidence_score</span>
              <span className="r-desc">Numeric confidence value used to indicate how much support exists for the signal.</span>
            </div>
            <div className="r-field">
              <span className="r-key">decision_id</span>
              <span className="r-desc">Unique identifier for the scored decision. Useful for internal logging and later outcome comparison.</span>
            </div>
            <div className="r-field">
              <span className="r-key">model_version</span>
              <span className="r-desc">Current production model identifier used for auditability and replay validation.</span>
            </div>
          </div>
        </section>

        <section className="impl-section">
          <div className="impl-section-label">
            <BarChart2 size={14} color="#D97642" />
            Model performance
          </div>
          <h2 className="impl-h2">Validated against real fare outcomes</h2>
          <p className="impl-body">
            The production model is continuously evaluated against observed fare outcomes using a delayed verification framework. Model lineage, verification outcomes, and deployment history are maintained for auditability.
          </p>

          <div className="proof-strip">
            <div className="proof-item">
              <span className="proof-number">t+7</span>
              <span className="proof-label">outcome verification window</span>
            </div>
            <div className="proof-item">
              <span className="proof-number">t+7</span>
              <span className="proof-label">outcomes verified against live fare movement</span>
            </div>
            <div className="proof-item">
              <span className="proof-number">audit</span>
              <span className="proof-label">model lineage tracked</span>
            </div>
          </div>
        </section>

        <div className="impl-cta">
          <div className="impl-cta-title">Run MIZAR in Shadow Mode</div>
          <div className="impl-cta-text">
            Start with an observation-only pilot. No checkout changes. No pricing changes.
            No customer-facing intervention until signal quality and commercial relevance
            have been reviewed.
          </div>
          <div className="impl-cta-btns">
            <button className="btn-primary" onClick={() => navigate('/contact')}>
              Discuss a pilot <ArrowRight size={16} />
            </button>
            <button className="btn-secondary" onClick={() => navigate('/about')}>
              Learn more
            </button>
          </div>
        </div>

      </div>

      <Footer />
    </div>
  );
}